import { useCallback } from 'react';

import { LogLevel } from 'features/analytics/enums';
import { sendAnalytics } from 'features/analytics/store';
import { useAppDispatch } from 'features/app/hooks';
import { useMediaStream } from 'features/media-streams/hooks';
import type { RtcConnection } from 'features/sip/interfaces';

export const useLocalVideo = () => {
  const { getInterpVideoStream, getPrivacyMediaStreamTrack } = useMediaStream();
  const dispatch = useAppDispatch();

  const sendPrivacyTrackToRemote = useCallback(
    async (rtcConnection: RtcConnection) => {
      const senders = rtcConnection.getSenders();
      const localRtpVideo = senders.find((s) => s.track?.kind === 'video');
      await localRtpVideo?.replaceTrack(getPrivacyMediaStreamTrack());
    },
    [getPrivacyMediaStreamTrack]
  );

  const sendVideoTrackToRemote = useCallback(
    async (rtcConnection: RtcConnection, stream: MediaStream) => {
      const senders = rtcConnection.getSenders();
      const localRtpVideo = senders.find((s) => s.track?.kind === 'video');

      const usedStream = stream ?? (await getInterpVideoStream());
      const [localVideoTrack] = usedStream.getVideoTracks();

      await localRtpVideo?.replaceTrack(localVideoTrack);
    },
    [getInterpVideoStream]
  );

  const stopRtcTracksAndCloseConnection = useCallback(
    (rtcConnection: RtcConnection) => {
      // Detach the local video feeds; note that we do NOT stop the tracks here since they are
      // most likely being managed through the SourceMediaTracks and are likely to be used elsewhere
      rtcConnection.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.replaceTrack(null);
        }
      });

      // stop the incoming video streams
      rtcConnection.getReceivers().forEach((receiver) => {
        receiver.track?.stop();
      });
      if (rtcConnection.signalingState !== 'closed') {
        dispatch(
          sendAnalytics({
            Method: 'stopRtcTracksAndCloseConnection',
            Message: 'RtcConnection is not closed at this point, closing it',
            Level: LogLevel.WARNING,
          })
        );
        rtcConnection.close();
      }
    },
    [dispatch]
  );

  return {
    sendPrivacyTrackToRemote,
    sendVideoTrackToRemote,
    stopRtcTracksAndCloseConnection,
  };
};
